import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Leaf, Info, ShoppingBag, Mail } from 'lucide-react';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/', icon: <Leaf size={18}/> },
    { name: 'About', href: '/about', icon: <Info size={18}/> },
    { name: 'Products', href: '/products', icon: <ShoppingBag size={18}/> },
    { name: 'Contact', href: '/contact', icon: <Mail size={18}/> },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'py-4 px-6 md:px-12' : 'py-8 px-6 md:px-12'
      }`}
    >
      <div 
        className={`max-w-7xl mx-auto flex items-center justify-between transition-all duration-500 ${
          scrolled ? 'glass rounded-full px-8 py-3 shadow-2xl backdrop-blur-md' : ''
        }`}
      >
        {/* Logo */}
        <Link 
          to="/"
          className="flex items-center gap-3 group cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <div>
            <img src="/logo.png" alt="Logo" className="w-auto h-[60px]" />
            </div>
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link, idx) => (
            <Link
              key={link.name}
              to={link.href}
              className={`text-sm font-bold uppercase tracking-widest transition-colors duration-300 relative group ${
                location.pathname === link.href ? 'text-primary' : 'text-slate-400 hover:text-white'
              }`}
            >
              {link.name}
              <span className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${location.pathname === link.href ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
            </Link>
          ))}
          
          <Link
            to="/contact"
            className="px-6 py-2.5 bg-primary hover:bg-primary-light text-white rounded-full font-bold syne tracking-widest transition-all duration-300 shadow-lg hover:shadow-primary/20 active:scale-95 text-[10px]"
          >
            INQUIRE NOW
          </Link>
        </div>

        {/* Mobile Toggle */}
        <div className="md:hidden">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass mt-4 rounded-3xl overflow-hidden border border-white/10"
          >
            <div className="flex flex-col p-6 gap-6">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-4 text-lg font-medium hover:text-primary"
                >
                  <span className="p-2 bg-white/5 rounded-lg text-primary">{link.icon}</span>
                  {link.name}
                </a>
              ))}
              <button className="w-full py-4 bg-primary text-white rounded-2xl font-bold shadow-xl shadow-primary/20">
                Inquire Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
